import { faCheck, faHeart, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FiveStars, MarkdownItComponent } from "Components";
import { LetteredUserCircle } from "Components/Shared/LetteredUserCircle";
import { ConceptConstants } from "Constants";
import { CaseActionTypeEnum, TechnologyVoteTypeEnum, VoteTypeEnum } from "Enums";
import { ICaseSelectedConcept, ICaseSelectedTechnology } from "Interfaces";
import React, { FC, useContext, useState } from "react"
import { CaseContext, AuthContext } from "Stores";
import { TConcept, TTechnology } from "Types";

import styles from "./overview.module.scss";
import { VoteProgressBar } from "./VoteProgressBar";

type TOverviewTableProps = {
    voteType: VoteTypeEnum
}

export const OverviewTable: FC<TOverviewTableProps> = ({ voteType }: TOverviewTableProps) => {
    const { authState } = useContext(AuthContext);
    const { caseState, caseDispatch } = useContext(CaseContext);
    const [chosenUsername, setChosenUsername] = useState(authState.userEmail);

    const handleConceptClick = (concept: TConcept) => {
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedConcept, selectedConceptId: concept.id,
            selectedEntityName: concept.name, selectedEntityNumbering: concept.numbering
        } as ICaseSelectedConcept);
    };

    const handleTechnologyClick = (technology: TTechnology) => {
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedTechnology, selectedTechnologyId: technology.id,
            selectedOriginalTechnologyId: technology.originalTechnologyId, selectedEntityName: technology.name,
            selectedEntityNumbering: technology.numbering
        } as ICaseSelectedTechnology);
    };

    // TODO: fix the complexity of this
    let usernames: string[] = [];
    if(caseState.conceptTechnologyList) {
        if(voteType === VoteTypeEnum.FiveStars) {
            for(const concept of caseState.conceptTechnologyList.concepts) {
                for(const technology of concept.technologies) {
                    if(technology.rates) {
                        if(technology.rates.currentUserRate) {
                            if(usernames.indexOf(technology.rates.currentUserRate.userName) < 0) {
                                usernames.push(technology.rates.currentUserRate.userName);
                            }
                        }

                        if(technology.rates.userRates) {
                            for(const rate of technology.rates.userRates) {
                                if(usernames.indexOf(rate.userName) < 0) {
                                    usernames.push(rate.userName);
                                }
                            }
                        }
                    }
                }
            }
        } else if(voteType === VoteTypeEnum.YesNoSuper) {
            for(const concept of caseState.conceptTechnologyList.concepts) {
                for(const technology of concept.technologies) {
                    if(technology.votes) {
                        if(technology.votes.currentUserVote) {
                            if(usernames.indexOf(technology.votes.currentUserVote.userName) < 0) {
                                usernames.push(technology.votes.currentUserVote.userName);
                            }
                        }

                        if(technology.votes.userVotes) {
                            for(const vote of technology.votes.userVotes) {
                                if(usernames.indexOf(vote.userName) < 0) {
                                    usernames.push(vote.userName);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeaderContainer}>
                <h2>Overview table</h2>
                <div className={styles.userCircleContainer}>
                    {usernames.map((username) => {
                        return (
                            <LetteredUserCircle key={username} username={username}
                                className={username === chosenUsername ? styles.selectedUserCircle : undefined}
                                onClick={() => { setChosenUsername(username); }}
                            />
                        );
                    })}
                </div>
            </div>

            <div className={[styles.toTable, styles.scrollbarCustomThin].join(" ")}>
                <table>
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={{ width: "30%" }}>Concept</th>
                            <th style={{ width: "70%" }}>Technology</th>
                            <th style={{ width: "1rem" }}>Selection</th>
                            <th style={{ width: "1rem" }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {caseState.conceptTechnologyList?.concepts.map((concept) => {
                            return (
                                <React.Fragment key={concept.id}>
                                    {concept.technologies.map((technology, technologyIndex) => {
                                        let voteAmount: number = 0;
                                        if (voteType === VoteTypeEnum.YesNoSuper) {
                                            voteAmount = technology.votes ? technology.votes.userVotes.length : 0;
                                            if (technology.votes?.currentUserVote) {
                                                voteAmount += 1;
                                            }
                                        } else if (voteType === VoteTypeEnum.FiveStars) {
                                            voteAmount = technology.rates ? technology.rates.userRates.length : 0;
                                            if (technology.rates?.currentUserRate) {
                                                voteAmount += 1;
                                            }
                                        }
                                        let superLikeAmount = technology.votes ? technology.votes.userVotes
                                            .filter(uv => uv.vote === TechnologyVoteTypeEnum.Super).length : 0;
                                        if (technology.votes?.currentUserVote?.vote === TechnologyVoteTypeEnum.Super) superLikeAmount += 1;

                                        let userVote = undefined;
                                        if (technology.votes) {
                                            let currentUserNameVotes = technology.votes.userVotes.filter(uv => uv.userName === chosenUsername);
                                            if (currentUserNameVotes.length > 0) {
                                                userVote = currentUserNameVotes[0].vote;
                                            } else {
                                                if (technology.votes.currentUserVote?.userName === chosenUsername) {
                                                    userVote = technology.votes.currentUserVote?.vote;
                                                }
                                            }
                                        }

                                        return (
                                            <tr key={technology.id}>
                                                {technologyIndex === 0 ?
                                                    <td className={styles.tableConceptColumn} rowSpan={concept.technologies.length}>
                                                        <span className={styles.clickableLink} onClick={() => { handleConceptClick(concept); }} >{concept.numbering ? concept.numbering : ""} {concept.name}</span> {concept.description ?
                                                            <>
                                                                <br />
                                                                <MarkdownItComponent source={concept.description} />
                                                            </>
                                                            : <></>}
                                                    </td>
                                                    : <></>}
                                                <td className={styles.tableTechnologyColumn} style={technologyIndex + 1 === concept.technologies.length ? { borderColor: "#757575" } : {}} >
                                                    <span className={styles.clickableLink} onClick={() => { handleTechnologyClick(technology); }}>{technology.numbering ? technology.numbering : ""} {technology.name}</span>
                                                </td>
                                                <td className={styles.tableSelectiontColumn} style={technologyIndex + 1 === concept.technologies.length ? { borderColor: "#757575" } : {}} >

                                                    {voteType === VoteTypeEnum.YesNoSuper ?
                                                        <div className={styles.selectionContainer}>
                                                            <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.No ? styles.votedNo : ""}`} icon={faTimes} />
                                                            <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.Yes ? styles.votedYes : ""}`} icon={faCheck} />
                                                            <span className="fa-layers fa-fw" style={{ minWidth: "1.25rem" }}>
                                                                <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.Super ? styles.votedSuperLike : ""}`} icon={faHeart} />
                                                                <span className={`fa-layers-text ${styles.fontAwesomeOverlayedNumber} ${styles.heartOverlay}`} >
                                                                    {superLikeAmount}
                                                                </span>
                                                            </span>
                                                            <VoteProgressBar progress={technology.votes?.averageVote} />
                                                        </div> : null}
                                                    {voteType === VoteTypeEnum.FiveStars ?
                                                        <div className={styles.selectionContainer}>
                                                            <FiveStars averageRating={technology.rates?.averageRate} />
                                                        </div> : null}

                                                </td>
                                                <td className={styles.tableVotesAmountColumn} style={technologyIndex + 1 === concept.technologies.length ? { borderColor: "#757575" } : {}} >
                                                    {voteAmount} of {usernames.length}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        {caseState.conceptTechnologyList && caseState.conceptTechnologyList.additionalTechnologies.length > 0 ?
                            <React.Fragment key={-1}>
                                {caseState.conceptTechnologyList.additionalTechnologies.map((technology, technologyIndex) => {
                                        const technologyCount = caseState.conceptTechnologyList ? caseState.conceptTechnologyList.additionalTechnologies.length : 0; 
                                        let voteAmount: number = 0;
                                        if (voteType === VoteTypeEnum.YesNoSuper) {
                                            voteAmount = technology.votes ? technology.votes.userVotes.length : 0;
                                            if (technology.votes?.currentUserVote) {
                                                voteAmount += 1;
                                            }
                                        } else if (voteType === VoteTypeEnum.FiveStars) {
                                            voteAmount = technology.rates ? technology.rates.userRates.length : 0;
                                            if (technology.rates?.currentUserRate) {
                                                voteAmount += 1;
                                            }
                                        }
                                        let superLikeAmount = technology.votes ? technology.votes.userVotes
                                            .filter(uv => uv.vote === TechnologyVoteTypeEnum.Super).length : 0;
                                        if (technology.votes?.currentUserVote?.vote === TechnologyVoteTypeEnum.Super) superLikeAmount += 1;

                                        let userVote = undefined;
                                        if (technology.votes) {
                                            let currentUserNameVotes = technology.votes.userVotes.filter(uv => uv.userName === chosenUsername);
                                            if (currentUserNameVotes.length > 0) {
                                                userVote = currentUserNameVotes[0].vote;
                                            } else {
                                                if (technology.votes.currentUserVote?.userName === chosenUsername) {
                                                    userVote = technology.votes.currentUserVote?.vote;
                                                }
                                            }
                                        }

                                        return (
                                            <tr key={technology.id}>
                                                {technologyIndex === 0 ?
                                                    <td className={styles.tableConceptColumn} rowSpan={technologyCount}>
                                                        <span className={styles.clickableLink} onClick={() => { handleConceptClick({ id: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID, name: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME, technologies: [], numbering: caseState.conceptTechnologyList?.concepts ? (caseState.conceptTechnologyList.concepts.length + 1) + "." : "" });  }}>
                                                        {caseState.conceptTechnologyList?.concepts ? (caseState.conceptTechnologyList?.concepts.length + 1) + "." : ""} {ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME}</span> 
                                                        <br />
                                                        <MarkdownItComponent source={ConceptConstants.FIXED_ADDITIONAL_CONCEPT_DESCRIPTION} />
                                                            
                                                    </td>
                                                    : <></>}
                                                <td className={styles.tableTechnologyColumn} style={technologyIndex + 1 === technologyCount ? { borderColor: "#757575" } : {}} >
                                                    <span className={styles.clickableLink} onClick={() => { handleTechnologyClick(technology); }}>{technology.numbering ? technology.numbering : ""} {technology.name}</span>
                                                </td>
                                                <td className={styles.tableSelectiontColumn} style={technologyIndex + 1 === technologyCount ? { borderColor: "#757575" } : {}} >

                                                    {voteType === VoteTypeEnum.YesNoSuper ?
                                                        <div className={styles.selectionContainer}>
                                                            <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.No ? styles.votedNo : ""}`} icon={faTimes} />
                                                            <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.Yes ? styles.votedYes : ""}`} icon={faCheck} />
                                                            <span className="fa-layers fa-fw" style={{ minWidth: "1.25rem" }}>
                                                                <FontAwesomeIcon className={`${styles.toTableVoteIcon} ${userVote === TechnologyVoteTypeEnum.Super ? styles.votedSuperLike : ""}`} icon={faHeart} />
                                                                <span className={`fa-layers-text ${styles.fontAwesomeOverlayedNumber} ${styles.heartOverlay}`} >
                                                                    {superLikeAmount}
                                                                </span>
                                                            </span>
                                                            <VoteProgressBar progress={technology.votes?.averageVote} />
                                                        </div> : null}
                                                    {voteType === VoteTypeEnum.FiveStars ?
                                                        <div className={styles.selectionContainer}>
                                                            <FiveStars averageRating={technology.rates?.averageRate} />
                                                        </div> : null}

                                                </td>
                                                <td className={styles.tableVotesAmountColumn} style={technologyIndex + 1 === technologyCount ? { borderColor: "#757575" } : {}} >
                                                    {voteAmount} of {usernames.length}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </React.Fragment>
                            : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}